import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { chaveDaCompra, grupoDeParcelas, linhaDe, podeAbater, podeSerAbatido } from '@domain/finance'
import type { OffsetLink, Transaction } from '@domain/finance'
import { dayAndMonth } from '@shared/date/civil'
import { sumCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { resumoDoAbatimento } from './candidatas'
import { FolhaAbaterComEntradas } from './FolhaAbaterComEntradas'
import { FolhaAbaterUmGasto } from './FolhaAbaterUmGasto'

/**
 * A seção "Abatimento" do detalhe — `Docs/05-Plano/Abatimento de gasto.md`.
 *
 * Num GASTO, as entradas que o abatem e o que fica com a casa; numa ENTRADA, o
 * gasto que ela abate. A conta é feita das entradas LIDAS, e não do `offset`
 * gravado: ele espera o gatilho, e a tela não deve mostrar o número velho
 * logo depois de a pessoa ter mudado.
 */
export function SecaoAbatimento({
  transaction,
  onVinculo,
}: {
  transaction: Transaction
  /** Depois que a folha da ENTRADA grava — o detalhe atualiza a transação. */
  onVinculo: (offsetOf: OffsetLink | null) => void
}) {
  const linha = linhaDe(transaction)
  if (podeSerAbatido(linha)) return <DoGasto gasto={transaction} />
  if (podeAbater(linha) || transaction.enrichment.offsetOf !== null) {
    return <DaEntrada entrada={transaction} onVinculo={onVinculo} />
  }
  return null
}

const SECAO = 'mt-4 border-t border-[var(--border)] pt-3'

function DoGasto({ gasto }: { gasto: Transaction }) {
  const { transactionRepository } = useFinanceRepositories()
  const chave = chaveDaCompra(gasto)
  const grupo = grupoDeParcelas(gasto)
  const [estado, setEstado] = useState<
    { entradas: Transaction[]; valorCents: number } | 'carregando' | 'falhou'
  >('carregando')
  const [aberta, setAberta] = useState(false)
  const [versao, setVersao] = useState(0)

  useEffect(() => {
    let vivo = true
    void Promise.all([
      transactionRepository.listOffsetEntries(chave),
      grupo === null ? Promise.resolve([gasto]) : transactionRepository.listByInstallmentGroup(grupo),
    ])
      .then(([entradas, linhas]) => {
        if (!vivo) return
        const vivas = linhas.filter((l) => podeSerAbatido(linhaDe(l)))
        setEstado({ entradas, valorCents: sumCents(vivas.map((l) => -l.source.amountCents)) })
      })
      .catch((erro: unknown) => {
        console.error('SecaoAbatimento: a leitura falhou', erro)
        if (vivo) setEstado('falhou')
      })
    return () => {
      vivo = false
    }
  }, [transactionRepository, chave, grupo, gasto, versao])

  if (estado === 'carregando') return null
  if (estado === 'falhou') {
    return (
      <p role="alert" className={`${SECAO} text-sm text-[var(--text-muted)]`}>
        Não foi possível carregar o abatimento agora.
      </p>
    )
  }

  const resumo = resumoDoAbatimento(estado.valorCents, estado.entradas)

  return (
    <section aria-label="Abatimento" className={SECAO}>
      <h2 className="mb-1 flex justify-between text-xs text-[var(--text-muted)]">
        <span>Abatimento</span>
        <span className="tabular-nums">{formatBRL(estado.valorCents)}</span>
      </h2>

      {resumo.vivas.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">Ninguém abateu esta compra.</p>
      ) : (
        <>
          <ul>
            {resumo.vivas.map((e) => (
              <li key={e.id} className="border-b border-[var(--border)] last:border-b-0">
                <Link
                  to="/transacoes/$id"
                  params={{ id: e.id }}
                  className="flex min-h-11 w-full items-center gap-2 text-sm"
                >
                  <span className="min-w-0 flex-1 truncate">
                    {`${e.source.description} · ${dayAndMonth(e.source.transactionDate)}`}
                  </span>
                  <span className="tabular-nums">{`− ${formatBRL(e.source.amountCents)}`}</span>
                </Link>
              </li>
            ))}
          </ul>
          {resumo.ficaCents === 0 ? (
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              Saldo zero: esta compra não entra nos gastos nem precisa de categoria.
            </p>
          ) : (
            <p className="mt-2 flex justify-between text-sm">
              <span className="text-[var(--text-muted)]">Fica com você</span>
              <span className="tabular-nums">{formatBRL(resumo.ficaCents)}</span>
            </p>
          )}
          {resumo.excessoCents > 0 && (
            <p className="mt-1 text-sm" style={{ color: 'var(--pendente)' }}>
              {`As entradas passam da compra em ${formatBRL(resumo.excessoCents)}.`}
            </p>
          )}
        </>
      )}

      <button
        type="button"
        className="alvo mt-1 text-sm text-[var(--brand)]"
        onClick={() => {
          setAberta(true)
        }}
      >
        {resumo.vivas.length === 0 ? 'Abater com entradas' : 'Mudar entradas'}
      </button>

      {aberta && (
        <FolhaAbaterComEntradas
          gasto={gasto}
          valorDaCompraCents={estado.valorCents}
          ligadas={estado.entradas}
          onFechar={() => {
            setAberta(false)
          }}
          onGravou={() => {
            setAberta(false)
            setVersao((v) => v + 1)
          }}
        />
      )}
    </section>
  )
}

function DaEntrada({
  entrada,
  onVinculo,
}: {
  entrada: Transaction
  onVinculo: (offsetOf: OffsetLink | null) => void
}) {
  const { transactionRepository } = useFinanceRepositories()
  const vinculo = entrada.enrichment.offsetOf
  // A COMPRA que a entrada abate, e não a linha guardada no vínculo: numa
  // parcelada, mostrar uma parcela ("−R$ 100,00") para um Pix que pagou as
  // quatro dizia o valor errado.
  const [compra, setCompra] = useState<{
    gasto: Transaction
    totalCents: number
    parcelas: number | null
  } | null>(null)
  const [aberta, setAberta] = useState(false)
  const [falhou, setFalhou] = useState(false)

  useEffect(() => {
    if (vinculo === null) return
    let vivo = true
    const ler = async () => {
      const gasto = await transactionRepository.byId(vinculo.transactionId)
      if (gasto === null) return null
      const grupo = grupoDeParcelas(gasto)
      const linhas =
        grupo === null ? [gasto] : await transactionRepository.listByInstallmentGroup(grupo)
      const vivas = linhas.filter((l) => podeSerAbatido(linhaDe(l)))
      return {
        gasto,
        totalCents: sumCents(vivas.map((l) => l.source.amountCents)),
        parcelas: gasto.source.installment?.total ?? null,
      }
    }
    ler()
      .then((lida) => {
        if (vivo) setCompra(lida)
      })
      .catch((erro: unknown) => {
        console.error('SecaoAbatimento: a leitura do gasto falhou', erro)
      })
    return () => {
      vivo = false
    }
  }, [transactionRepository, vinculo])

  const desfazer = () => {
    setFalhou(false)
    transactionRepository
      .setOffsetLinks([{ id: entrada.id, offsetOf: null }])
      .then(() => {
        onVinculo(null)
      })
      .catch(() => {
        setFalhou(true)
      })
  }

  return (
    <section aria-label="Abatimento" className={SECAO}>
      <h2 className="mb-1 text-xs text-[var(--text-muted)]">Abatimento</h2>
      {vinculo === null ? (
        <>
          <p className="text-sm text-[var(--text-muted)]">Esta entrada não abate nenhum gasto.</p>
          <button
            type="button"
            className="alvo mt-1 text-sm text-[var(--brand)]"
            onClick={() => {
              setAberta(true)
            }}
          >
            Abater um gasto
          </button>
        </>
      ) : (
        <>
          <Link
            to="/transacoes/$id"
            params={{ id: vinculo.transactionId }}
            className="flex min-h-11 w-full items-center gap-2 text-sm"
          >
            <span className="min-w-0 flex-1 truncate">
              {`Abate: ${vinculo.description}`}
              {compra !== null && ` · ${dayAndMonth(compra.gasto.source.transactionDate)}`}
              {compra?.parcelas != null && ` · em ${String(compra.parcelas)}x`}
            </span>
            {compra !== null && (
              <span className="tabular-nums">{formatBRL(compra.totalCents)}</span>
            )}
          </Link>
          <button type="button" className="alvo text-sm text-[var(--text-muted)]" onClick={desfazer}>
            Desfazer
          </button>
        </>
      )}
      {falhou && (
        <p role="alert" className="text-sm text-[var(--text-muted)]">
          Não foi possível salvar agora.
        </p>
      )}
      {aberta && (
        <FolhaAbaterUmGasto
          entrada={entrada}
          onFechar={() => {
            setAberta(false)
          }}
          onGravou={(offsetOf) => {
            setAberta(false)
            onVinculo(offsetOf)
          }}
        />
      )}
    </section>
  )
}
